import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { OrgProvider, useOrg } from "@/contexts/OrgContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import api from "@/lib/api";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Promoters from "@/pages/Promoters";
import PromoterDetail from "@/pages/PromoterDetail";
import PromoterForm from "@/pages/PromoterForm";
import Clients from "@/pages/Clients";
import Events from "@/pages/Events";
import EventForm from "@/pages/EventForm";
import EventDetail from "@/pages/EventDetail";
import Notifications from "@/pages/Notifications";
import Settings from "@/pages/Settings";
import Invoices from "@/pages/Invoices";
import Alerts from "@/pages/Alerts";
import Templates from "@/pages/Templates";
import Stocks from "@/pages/Stocks";
import PublicResponse from "@/pages/PublicResponse";
import SuperadminOrganizations from "@/pages/SuperadminOrganizations";
import SuperadminMetrics from "@/pages/SuperadminMetrics";
import SuperadminMessageLog from "@/pages/SuperadminMessageLog";
import SuperadminOrgWhatsapp from "@/pages/SuperadminOrgWhatsapp";
import Users from "@/pages/Users";
import AcceptInvitation from "@/pages/AcceptInvitation";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import WhatsappSettings from "@/pages/WhatsappSettings";
import WhatsappInbox from "@/pages/WhatsappInbox";
import Onboarding from "@/pages/Onboarding";
import { Toaster } from "@/components/ui/sonner";

// After login, send user to their tenant slug (or onboarding if the org
// still has an incomplete wizard AND the user is admin).
function RootRedirect() {
  const { user, loading } = useAuth();
  const { org, loading: orgLoading } = useOrg();
  const [onbState, setOnbState] = useState(undefined); // undefined = not-yet-checked
  useEffect(() => {
    // Only kick off the fetch when we truly have an admin + org context.
    // Leaving onbState=undefined during the transient state prevents an
    // accidental "should redirect to dashboard" flash before the API returns.
    if (!user || user.role !== "admin" || !org?.slug) return;
    let cancel = false;
    api.get("/organizations/me/onboarding-status")
      .then((r) => { if (!cancel) setOnbState(r.data); })
      .catch(() => { if (!cancel) setOnbState(null); });
    return () => { cancel = true; };
  }, [user, org]);

  if (loading || orgLoading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "superadmin") return <Navigate to="/admin/organizations" replace />;
  if (!org?.slug) return null;
  // Admins wait for the onboarding-status result before we route them.
  if (user.role === "admin" && onbState === undefined) return null;
  if (onbState && !onbState.should_hide_checklist && onbState.progress?.done < onbState.progress?.total) {
    return <Navigate to={`/${org.slug}/onboarding`} replace />;
  }
  return <Navigate to={`/${org.slug}/dashboard`} replace />;
}

// Tenant boundary: ensure the slug in the URL matches the user's own org.
// Superadmin can navigate any tenant.
function TenantGuard({ children }) {
  const { slug } = useParams();
  const { user } = useAuth();
  const { org } = useOrg();
  if (user?.role === "superadmin") return children;
  if (!org) return null;
  if (slug !== org.slug) return <Navigate to={`/${org.slug}/dashboard`} replace />;
  return children;
}

function SuperadminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "superadmin") return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <OrgProvider>
            <ThemeProvider>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/p/:token" element={<PublicResponse />} />
              <Route path="/accept-invitation" element={<AcceptInvitation />} />

              {/* Superadmin platform-level pages (no tenant slug) */}
              <Route
                path="/admin/organizations"
                element={
                  <SuperadminRoute>
                    <Layout platformView />
                  </SuperadminRoute>
                }
              >
                <Route index element={<SuperadminOrganizations />} />
                <Route path=":orgId/whatsapp/manual" element={<SuperadminOrgWhatsapp />} />
              </Route>

              {/* Superadmin usage metrics (no tenant slug) */}
              <Route
                path="/admin/metrics"
                element={
                  <SuperadminRoute>
                    <Layout platformView />
                  </SuperadminRoute>
                }
              >
                <Route index element={<SuperadminMetrics />} />
              </Route>

              <Route
                path="/admin/messages"
                element={
                  <SuperadminRoute>
                    <Layout platformView />
                  </SuperadminRoute>
                }
              >
                <Route index element={<SuperadminMessageLog />} />
              </Route>

              {/* Tenant pages — path-based routing under /:slug/* */}
              <Route
                path="/:slug"
                element={
                  <ProtectedRoute>
                    <TenantGuard>
                      <Layout />
                    </TenantGuard>
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="promotores" element={<Promoters />} />
                <Route path="inbox" element={<WhatsappInbox />} />
                <Route path="promotores/novo" element={<PromoterForm />} />
                <Route path="promotores/:id" element={<PromoterDetail />} />
                <Route path="promotores/:id/editar" element={<PromoterForm />} />
                <Route path="clientes" element={<Clients />} />
                <Route path="eventos" element={<Events />} />
                <Route path="eventos/novo" element={<EventForm />} />
                <Route path="eventos/:id" element={<EventDetail />} />
                <Route path="eventos/:id/editar" element={<EventForm />} />
                <Route path="notificacoes" element={<Notifications />} />
                <Route path="faturacao" element={<Invoices />} />
                <Route path="alertas" element={<Alerts />} />
                <Route path="templates" element={<Templates />} />
                <Route path="stocks" element={<Stocks />} />
                <Route path="utilizadores" element={<Users />} />
                <Route path="configuracoes" element={<Settings />} />
                <Route path="configuracoes/whatsapp" element={<WhatsappSettings />} />
                <Route path="onboarding" element={<Onboarding />} />
              </Route>

              <Route path="/" element={<RootRedirect />} />
              <Route path="*" element={<RootRedirect />} />
            </Routes>
            <Toaster />
            </ThemeProvider>
          </OrgProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
